// Autometrics Logistics Performance dashboard (v3).
// One shell shared by six pages; each page is its own route so it can be
// placed under its own sidebar menu. Filters persist across pages.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  useAm, amActions, useFilteredRows, facetOptions, FILTER_DEFS, FILTER_BY_KEY, FILTER_GROUPS,
  activeFilterCount, AM_ROUTES,
} from './amStore';
import { MultiSelect, fmtInt } from './amUi';
import ExecutivePage from './pages/ExecutivePage';
import NetworkPage from './pages/NetworkPage';
import FtlPage from './pages/FtlPage';
import PodPage from './pages/PodPage';
import Co2Page from './pages/Co2Page';
import ExplorerPage from './pages/ExplorerPage';
import './autometrics.css';

export const AM_PAGES = {
  executive: { title: 'Executive Performance', subtitle: 'On-time delivery, exceptions, 3PL and lane performance', Component: ExecutivePage },
  network: { title: 'Transport Network', subtitle: 'Primary vs secondary, PTL vs FTL, transport category and organised share', Component: NetworkPage },
  ftl: { title: 'FTL Utilization', subtitle: 'Trip-level weight and volume utilization by vehicle, lane and transporter', Component: FtlPage },
  pod: { title: 'POD Compliance', subtitle: 'Proof of delivery received vs pending, ageing and transporter backlog', Component: PodPage },
  co2: { title: 'CO₂ Performance', subtitle: 'Emissions and intensity against the 2024 baseline', Component: Co2Page },
  explorer: { title: 'Shipment & Trip Detail', subtitle: 'Data quality checks and the row-level shipment explorer', Component: ExplorerPage },
};

function FilterPanel({ s }) {
  const [search, setSearch] = useState(s.search);
  useEffect(() => { setSearch(s.search); }, [s.search]);
  useEffect(() => {
    const t = setTimeout(() => { if (search !== s.search) amActions.setSearch(search); }, 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = s.filtersOpen;
  const active = FILTER_DEFS.reduce((n, d) => n + s.filters[d.key].length, 0);
  return (
    <div className="am-filters">
      <div className="am-filter-head">
        <div className="am-search">
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" /><path d="m20 20-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search LR, invoice, vehicle or trip" />
        </div>
        <span className="am-filter-hint">{FILTER_DEFS.length} filters · every filter applies to every page · counts update with your selection</span>
        <button type="button" className={`am-btn ghost sm ${open ? 'on' : ''}`} onClick={() => amActions.setFiltersOpen(!open)}>
          {open ? 'Hide filters' : `Show filters${active ? ` (${active} active)` : ''}`}
        </button>
      </div>
      {open && (
        <div className="am-filter-groups">
          {FILTER_GROUPS.map((g) => (
            <div key={g} className="am-filter-group">
              <div className="am-filter-group-label">{g}</div>
              <div className="am-filter-grid">
                {FILTER_DEFS.filter((d) => d.group === g).map((d) => (
                  <MultiSelect key={d.key} label={d.label} selected={s.filters[d.key]}
                    getOptions={() => facetOptions(s, d.key)}
                    onChange={(v) => amActions.setFilter(d.key, v)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Chips({ s, total }) {
  const chips = [];
  FILTER_DEFS.forEach((d) => s.filters[d.key].forEach((v) => chips.push({ key: d.key, value: v, label: d.label, text: d.format ? d.format(v) : v })));
  const n = activeFilterCount(s);
  return (
    <div className="am-chips">
      <span className="am-chips-count"><b>{fmtInt(total)}</b> shipments in view</span>
      {s.search.trim() && (
        <span className="am-chip">Search: <b>{s.search}</b>
          <button type="button" aria-label="Remove search" onClick={() => amActions.setSearch('')}>×</button>
        </span>
      )}
      {chips.map((c) => (
        <span className="am-chip" key={`${c.key}-${c.value}`}>{c.label}: <b>{c.text}</b>
          <button type="button" aria-label={`Remove ${c.label} ${c.text}`} onClick={() => amActions.removeValue(c.key, c.value)}>×</button>
        </span>
      ))}
      {n === 0 && <span className="am-chips-none">No filters applied. Click any bar, row or card to filter the whole dashboard.</span>}
      {n > 0 && <button type="button" className="am-btn reset" onClick={amActions.reset}>Reset filters</button>}
    </div>
  );
}

export default function AutometricsDashboard({ page = 'executive' }) {
  const s = useAm();
  const navigate = useNavigate();
  const [range, setRange] = useState(s.range);
  useEffect(() => { amActions.ensureLoaded(); }, []);
  const rows = useFilteredRows(s);
  const meta = AM_PAGES[page] || AM_PAGES.executive;
  const Page = meta.Component;

  const drillToExplorer = useCallback((patch = {}, opts = {}) => {
    if (Object.keys(patch).length) amActions.apply(patch);
    if (opts.sort) amActions.setExplorerSort(opts.sort);
    navigate(AM_ROUTES.explorer);
    window.scrollTo(0, 0);
  }, [navigate]);
  const pathToPatch = (path) => Object.fromEntries(path.map((p) => [p.filterKey, [p.value]]));
  const ctx = { s, rows, drillToExplorer, drillPath: (path, extra = {}) => drillToExplorer({ ...pathToPatch(path), ...extra }) };

  const rangeChanged = range.from && range.to
    && (range.from.getTime() !== s.range.from.getTime() || range.to.getTime() !== s.range.to.getTime());

  return (
    <div className="am-root">
      <div className="am-top">
        <div className="am-title">
          <div className="am-eyebrow">Autometrics · Logistics performance</div>
          <h1>{meta.title}</h1>
          <p>{meta.subtitle}</p>
        </div>
        <div className="am-window">
          <label>Dispatch date</label>
          <div className="am-window-row">
            <DatePicker selected={range.from} onChange={(d) => setRange((r) => ({ ...r, from: d }))} dateFormat="dd-MMM-yyyy" className="am-date" maxDate={range.to} popperPlacement="bottom-end" popperClassName="am-date-pop" />
            <span>to</span>
            <DatePicker selected={range.to} onChange={(d) => setRange((r) => ({ ...r, to: d }))} dateFormat="dd-MMM-yyyy" className="am-date" minDate={range.from} popperPlacement="bottom-end" popperClassName="am-date-pop" />
            <button type="button" className="am-btn primary" disabled={s.status === 'loading'}
              onClick={() => amActions.load({ from: range.from, to: range.to }, { refresh: !rangeChanged })}>
              {s.status === 'loading' ? 'Loading…' : rangeChanged ? 'Apply' : 'Refresh'}
            </button>
          </div>
          {s.refreshedAt && (
            <small>
              Refreshed {s.refreshedAt.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              {s.loadInfo && ` · loaded in ${s.loadInfo.secs.toFixed(1)} s${s.loadInfo.cache === 'hit' ? ' (cached)' : ''}`}
            </small>
          )}
        </div>
      </div>

      <FilterPanel s={s} />
      <div className="am-sticky">
        <Chips s={s} total={rows.length} />
      </div>

      {s.status === 'error' && (
        <div className="am-state error">
          <b>{s.error}</b>
          <button type="button" className="am-btn primary" onClick={() => amActions.load()}>Try again</button>
        </div>
      )}
      {(s.status === 'loading' || s.status === 'idle') && s.rows.length === 0 && (
        <div className="am-state">
          <div className="am-spinner" />
          Loading shipments for {s.range.from.toLocaleDateString('en-IN')} to {s.range.to.toLocaleDateString('en-IN')}…
        </div>
      )}
      {s.rows.length > 0 && (
        <main className={`am-main ${s.status === 'loading' ? 'busy' : ''}`}>
          <Page ctx={ctx} />
        </main>
      )}
      {s.status === 'ready' && s.rows.length === 0 && (
        <div className="am-state">No shipments were dispatched in this date range.</div>
      )}
    </div>
  );
}

// Route wrappers - one per sidebar menu item.
export const AutometricsExecutive = () => <AutometricsDashboard page="executive" />;
export const AutometricsNetwork = () => <AutometricsDashboard page="network" />;
export const AutometricsFtl = () => <AutometricsDashboard page="ftl" />;
export const AutometricsPod = () => <AutometricsDashboard page="pod" />;
export const AutometricsCo2 = () => <AutometricsDashboard page="co2" />;
export const AutometricsExplorer = () => <AutometricsDashboard page="explorer" />;
export { FILTER_BY_KEY };
